import * as React from "react";
import { useCallback, useMemo, useRef } from "react";
import { AlertCircle, Loader2, XIcon } from "lucide-react";

import {
  PINE_SCRIPT_VIEW_MAX_H,
  PineScriptCodeBlock,
} from "../../../components/Reports/PineScriptCodeBlock";
import { CopyIcon, type CopyIconHandle } from "@/components/ui/copy-icon";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export type StrategyPineScriptViewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  loading: boolean;
  error: string | null;
  pineText: string;
  onCopy: () => void;
};

export function StrategyPineScriptViewDialog({
  open,
  onOpenChange,
  title,
  loading,
  error,
  pineText,
  onCopy,
}: StrategyPineScriptViewDialogProps): React.ReactElement {
  const displayTitle = title.trim() || "version";
  const canCopy = Boolean(pineText.trim()) && !loading;
  const copyIconRef = useRef<CopyIconHandle | null>(null);

  const startCopyIconAnimation = useCallback(() => {
    void copyIconRef.current?.startAnimation();
  }, []);

  const stopCopyIconAnimation = useCallback(() => {
    void copyIconRef.current?.stopAnimation();
  }, []);

  const copyIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startCopyIconAnimation,
      onMouseEnter: startCopyIconAnimation,
      onMouseLeave: stopCopyIconAnimation,
    }),
    [startCopyIconAnimation, stopCopyIconAnimation]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        data-policy-view-dialog=""
        data-strategy-pine-script-view-dialog=""
        className={cn("flex max-h-[min(90vh,48rem)] flex-col sm:max-w-6xl")}
      >
        <DialogClose asChild>
          <button
            type="button"
            data-policy-view-dismiss=""
            aria-label="Close"
            className="absolute top-4 right-4 inline-flex size-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-white dark:hover:bg-white/5 dark:hover:text-white"
          >
            <XIcon className="size-4" />
          </button>
        </DialogClose>

        <DialogHeader className="shrink-0">
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">
            Pinescript ({displayTitle})
          </DialogTitle>
          <DialogDescription className="font-[family-name:var(--font-primary)] font-normal text-gray-500 dark:text-white">
            TradingView Pinescript generated from this policy version.
          </DialogDescription>
        </DialogHeader>

        <div
          data-strategy-pine-script-view-dialog-body=""
          className="min-h-0 flex-1 overflow-y-auto"
        >
          {error ? (
            <Alert variant="error">
              <AlertCircle aria-hidden />
              <AlertDescription className="font-[family-name:var(--font-primary)]">
                {error}
              </AlertDescription>
            </Alert>
          ) : loading ? (
            <div
              data-strategy-pine-script-view-dialog-status=""
              className="flex items-center gap-3 py-6"
            >
              <Loader2
                className="size-4 shrink-0 animate-spin text-muted-foreground dark:text-white"
                aria-hidden
              />
              <p className="font-[family-name:var(--font-primary)] text-sm text-muted-foreground dark:text-white">
                Loading Pinescript…
              </p>
            </div>
          ) : pineText ? (
            <PineScriptCodeBlock code={pineText} maxH={PINE_SCRIPT_VIEW_MAX_H} brandChrome />
          ) : (
            <p
              data-strategy-pine-script-view-dialog-status=""
              className="font-[family-name:var(--font-primary)] text-sm text-muted-foreground dark:text-white"
            >
              No Pinescript returned.
            </p>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:justify-end">
          <button
            type="button"
            data-policy-view-copy=""
            aria-label="Copy Pinescript"
            disabled={!canCopy}
            onClick={onCopy}
            {...copyIconHoverHandlers}
          >
            <CopyIcon ref={copyIconRef} size={16} className="shrink-0 text-current" aria-hidden />
          </button>
          <DialogClose asChild>
            <button type="button" data-policy-view-close="">
              Close
            </button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
